import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Card, Col, Row, Select, Space, Typography } from "antd";
import { rgbaColor } from "helpers/utils";
import ReactEchart from "components/common/ReactEchart";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { useAppContext } from "providers/AppProvider";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
]);

const RANGE_OPTIONS = [
  { key: "last7Days", label: "Last 7 days" },
  { key: "lastMonth", label: "Last 30 days" },
  { key: "lastYear", label: "Last 12 months" },
  { key: "byMonth", label: "By Month" },
  { key: "byYear", label: "By Year" },
];

const parseCreatedAt = (user) => {
  const value =
    user?.createdAt ||
    user?.createdOn ||
    user?.created_at ||
    user?.created ||
    user?.dateCreated ||
    user?.acceptedAt ||
    user?.securityPhotoVerification?.uploadedOn ||
    user?.securityPhotoVerification?.uploadedAt ||
    user?.identityUser?.createdAt ||
    user?.identityUser?.createdOn ||
    user?.profileData?.createdAt ||
    user?.profile?.createdAt;
  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "object") {
    const millis =
      (typeof value.epochMillis === "number" && value.epochMillis) ||
      (typeof value.millis === "number" && value.millis) ||
      (typeof value.timestamp === "number" && value.timestamp) ||
      null;
    if (millis) {
      const d = new Date(millis);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const humanMonthTime = trimmed.match(
      /^([A-Za-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s*(\d{4})\s*,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]m)?$/i,
    );
    if (humanMonthTime) {
      const [, mon, dd, yyyy, hh, mm, ss, ap] = humanMonthTime;
      const hasAmPm = Boolean(ap);
      const normalizedHuman = `${mon} ${dd}, ${yyyy} ${hh}:${mm}${ss ? `:${ss}` : ""}${hasAmPm ? ` ${ap.toUpperCase()}` : ""}`;
      const d = new Date(normalizedHuman);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const normalized = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(trimmed)
      ? trimmed.replace(" ", "T")
      : trimmed;

    const dmy = normalized.match(/^(\d{2})[-/](\d{2})[-/](\d{4})(?:\s|T|$)(.*)$/);
    if (dmy) {
      const [, dd, mm, yyyy, rest] = dmy;
      const iso = `${yyyy}-${mm}-${dd}${rest ? `T${rest.replace(/^T/, "")}` : ""}`;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const countUsersWithValidCreatedAt = (users = []) =>
  users.reduce((acc, u) => (parseCreatedAt(u) ? acc + 1 : acc), 0);

const getAvailableYears = (users = []) => {
  const years = new Set();
  users.forEach((u) => {
    const d = parseCreatedAt(u);
    if (!d) return;
    years.add(d.getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
};

const monthName = (monthIndex) =>
  dayjs().month(monthIndex).format("MMM");

const buildMonthlySeriesForYear = (users = [], year) => {
  const series = Array.from({ length: 12 }, () => 0);
  users.forEach((u) => {
    const d = parseCreatedAt(u);
    if (!d) return;
    if (d.getFullYear() !== year) return;
    series[d.getMonth()] += 1;
  });
  return {
    labels: Array.from({ length: 12 }, (_, i) => monthName(i)),
    series,
  };
};

const buildDailySeriesForMonth = (users = [], year, monthIndex) => {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const series = Array.from({ length: daysInMonth }, () => 0);
  users.forEach((u) => {
    const d = parseCreatedAt(u);
    if (!d) return;
    if (d.getFullYear() !== year) return;
    if (d.getMonth() !== monthIndex) return;
    series[d.getDate() - 1] += 1;
  });
  return {
    labels: Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`),
    series,
  };
};

const metricFallback = {
  value: "-",
  percentage: "0.0%",
  up: true,
};

const pctLabelFromTotals = (currentTotal, previousTotal) => {
  const cur = Number(currentTotal) || 0;
  const prev = Number(previousTotal) || 0;
  if (!prev) return { percentage: "0.0%", up: true };
  const pct = ((cur - prev) / prev) * 100;
  if (!Number.isFinite(pct)) return { percentage: "0.0%", up: true };
  return { percentage: `${Math.abs(pct).toFixed(1)}%`, up: pct >= 0 };
};

const getOptions = (getThemeColor, labels, currentSeries, previousSeries) => ({
  color: [getThemeColor("primary"), getThemeColor("info")],
  tooltip: {
    trigger: "axis",
    padding: [7, 10],
    backgroundColor: getThemeColor("gray-100"),
    borderColor: getThemeColor("gray-300"),
    textStyle: { color: getThemeColor("gray-1100") },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: { type: "none" },
    formatter: (params) => {
      const cur = params?.[0]?.value ?? 0;
      const prev = params?.[1]?.value ?? 0;
      const axis = params?.[0]?.axisValue ?? "";
      const pct = prev ? ((cur - prev) / prev) * 100 : 0;
      const pctOk = Number.isFinite(pct) ? pct : 0;
      const isDown = pctOk < 0;
      return `<div>
        <p class='mb-0 fs-11 text-600'>${axis}</p>
        <div class="d-flex align-items-center">
          <p class='mb-0 text-600 fs-10'>
            Users: <span class='text-800 fw-semibold fs-10'>${cur}</span>
          </p>
          <div class="d-flex align-items-center ms-2">
            <h6 class="fs-11 mb-0 ms-1 fw-semibold">
              <span style="color:${isDown ? getThemeColor("danger") : getThemeColor("success")}">
                ${isDown ? "&#9660;" : "&#9650;"}
              </span>
              ${Math.abs(pctOk).toFixed(2)} %
            </h6>
          </div>
        </div>
      </div>`;
    },
  },
  xAxis: {
    type: "category",
    data: labels,
    axisLabel: {
      color: getThemeColor("gray-600"),
      fontSize: 11,
      padding: [0, 0, 0, 5],
      showMaxLabel: false,
    },
    axisLine: { lineStyle: { color: getThemeColor("gray-200") } },
    axisTick: { show: true, length: 18, lineStyle: { color: getThemeColor("gray-200") } },
    boundaryGap: false,
  },
  yAxis: {
    position: "right",
    axisPointer: { type: "none" },
    axisTick: "none",
    splitLine: { lineStyle: { color: getThemeColor("gray-200") } },
    axisLine: { show: false },
    axisLabel: { color: getThemeColor("gray-600") },
  },
  series: [
    {
      type: "line",
      data: currentSeries,
      showSymbol: false,
      symbol: "circle",
      animation: false,
      itemStyle: { borderColor: getThemeColor("primary"), borderWidth: 2 },
      lineStyle: { color: getThemeColor("primary") },
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: rgbaColor(getThemeColor("primary"), 0.2) },
            { offset: 1, color: rgbaColor(getThemeColor("primary"), 0) },
          ],
        },
      },
    },
    {
      type: "line",
      data: previousSeries,
      symbol: "none",
      lineStyle: { type: "dashed", width: 1, color: getThemeColor("info") },
    },
  ],
  grid: { containLabel: true, right: "5px", left: 0, bottom: 0, top: "10px" },
});

const MetricSummary = ({ title, value, percentage, up }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {title}
    </Typography.Text>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <Typography.Text strong style={{ fontSize: 20, lineHeight: 1.2 }}>
        {value}
      </Typography.Text>
      <Space size={6} align="center">
        <FontAwesomeIcon
          icon={up ? "caret-up" : "caret-down"}
          style={{ color: up ? "#52c41a" : "#faad14" }}
        />
        <Typography.Text style={{ fontSize: 12, color: up ? "#52c41a" : "#faad14" }}>
          {percentage}
        </Typography.Text>
      </Space>
    </div>
  </div>
);

MetricSummary.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  percentage: PropTypes.string.isRequired,
  up: PropTypes.bool,
};

const SegmentTile = ({ title, value, percentage, up, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      appearance: "none",
      border: active ? "1px solid rgba(22, 119, 255, 0.45)" : "1px solid rgba(5, 5, 5, 0.10)",
      background: active ? "rgba(22, 119, 255, 0.06)" : "rgba(255,255,255,0.85)",
      borderRadius: 12,
      padding: "10px 12px",
      textAlign: "left",
      cursor: "pointer",
      width: "100%",
    }}
  >
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {title}
    </Typography.Text>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <Typography.Text strong style={{ fontSize: 18, lineHeight: 1.1 }}>
        {value}
      </Typography.Text>
      <Space size={6} align="center">
        <FontAwesomeIcon
          icon={up ? "caret-up" : "caret-down"}
          style={{ color: up ? "#52c41a" : "#faad14" }}
        />
        <Typography.Text style={{ fontSize: 12, color: up ? "#52c41a" : "#faad14" }}>
          {percentage}
        </Typography.Text>
      </Space>
    </div>
  </button>
);

SegmentTile.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  percentage: PropTypes.string.isRequired,
  up: PropTypes.bool,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default function UserGrowthAudienceCard({
  growthRanges,
  growthRangesCustomers,
  growthRangesStaff,
  growthRangesUnclassified,
  totalUsersLabel,
  users,
  usersCustomers,
  usersStaff,
  usersUnclassified,
  className,
}) {
  const { getThemeColor } = useAppContext();
  const [rangeKey, setRangeKey] = useState("last7Days");
  const [segment, setSegment] = useState("all"); // all | customers | staff | unclassified
  const availableYears = useMemo(() => getAvailableYears(users || []), [users]);
  const defaultYear = availableYears[0] || new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  // keep year/month sane when data changes
  React.useEffect(() => {
    if (!availableYears.length) return;
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears.join(",")]);

  const resolvedGrowthRanges = useMemo(() => {
    if (segment === "customers") return growthRangesCustomers || null;
    if (segment === "staff") return growthRangesStaff || null;
    if (segment === "unclassified") return growthRangesUnclassified || null;
    return growthRanges || null;
  }, [
    growthRanges,
    growthRangesCustomers,
    growthRangesStaff,
    growthRangesUnclassified,
    segment,
  ]);

  const resolvedUsersList = useMemo(() => {
    if (segment === "customers") return usersCustomers || [];
    if (segment === "staff") return usersStaff || [];
    if (segment === "unclassified") return usersUnclassified || [];
    return users || [];
  }, [segment, users, usersCustomers, usersStaff, usersUnclassified]);

  const usersForSegment = (seg) => {
    if (seg === "customers") return usersCustomers || [];
    if (seg === "staff") return usersStaff || [];
    if (seg === "unclassified") return usersUnclassified || [];
    return users || [];
  };

  const getSegmentMetric = (seg) => {
    const resolvedRanges =
      seg === "customers"
        ? growthRangesCustomers
        : seg === "staff"
          ? growthRangesStaff
          : seg === "unclassified"
            ? growthRangesUnclassified
            : growthRanges;
    const selected = resolvedRanges?.[rangeKey];
    const fallback = metricFallback;
    if (selected) {
      return {
        percentage: selected?.label ?? fallback.percentage,
        up: typeof selected?.up === "boolean" ? selected.up : fallback.up,
      };
    }

    if (rangeKey === "byYear") {
      const list = usersForSegment(seg);
      const current = buildMonthlySeriesForYear(list || [], selectedYear);
      const prev = buildMonthlySeriesForYear(list || [], selectedYear - 1);
      return pctLabelFromTotals(
        current.series.reduce((a, n) => a + (Number(n) || 0), 0),
        prev.series.reduce((a, n) => a + (Number(n) || 0), 0),
      );
    }

    if (rangeKey === "byMonth") {
      const list = usersForSegment(seg);
      const current = buildDailySeriesForMonth(list || [], selectedYear, selectedMonth);
      const prevDate = new Date(selectedYear, selectedMonth - 1, 1);
      const prev = buildDailySeriesForMonth(
        list || [],
        prevDate.getFullYear(),
        prevDate.getMonth(),
      );
      return pctLabelFromTotals(
        current.series.reduce((a, n) => a + (Number(n) || 0), 0),
        prev.series.reduce((a, n) => a + (Number(n) || 0), 0),
      );
    }

    return { percentage: fallback.percentage, up: fallback.up };
  };

  const segmentTiles = useMemo(() => {
    const counts = {
      all: Array.isArray(users) ? users.length : 0,
      customers: Array.isArray(usersCustomers) ? usersCustomers.length : 0,
      staff: Array.isArray(usersStaff) ? usersStaff.length : 0,
      unclassified: Array.isArray(usersUnclassified) ? usersUnclassified.length : 0,
    };
    return [
      { key: "all", title: "All", value: String(counts.all), ...getSegmentMetric("all") },
      {
        key: "customers",
        title: "Customers",
        value: String(counts.customers),
        ...getSegmentMetric("customers"),
      },
      { key: "staff", title: "Staff", value: String(counts.staff), ...getSegmentMetric("staff") },
      {
        key: "unclassified",
        title: "Unclassified",
        value: String(counts.unclassified),
        ...getSegmentMetric("unclassified"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, usersCustomers, usersStaff, usersUnclassified, rangeKey, growthRanges, growthRangesCustomers, growthRangesStaff, growthRangesUnclassified]);

  const chartInput = useMemo(() => {
    // For explicit Year/Month filters: show true calendar buckets.
    if (rangeKey === "byYear") {
      const current = buildMonthlySeriesForYear(resolvedUsersList || [], selectedYear);
      const prev = buildMonthlySeriesForYear(resolvedUsersList || [], selectedYear - 1);
      return {
        labels: current.labels,
        currentSeries: current.series,
        previousSeries: prev.series,
      };
    }

    if (rangeKey === "byMonth") {
      const current = buildDailySeriesForMonth(
        resolvedUsersList || [],
        selectedYear,
        selectedMonth,
      );
      const prevDate = new Date(selectedYear, selectedMonth - 1, 1);
      const prev = buildDailySeriesForMonth(
        resolvedUsersList || [],
        prevDate.getFullYear(),
        prevDate.getMonth(),
      );
      return {
        labels: current.labels,
        currentSeries: current.series,
        previousSeries: prev.series.slice(0, current.series.length),
      };
    }

    const selected = resolvedGrowthRanges?.[rangeKey];
    if (!selected) {
      return { labels: [], currentSeries: [], previousSeries: [] };
    }
    const labels = Array.isArray(selected.labels) ? selected.labels : [];
    const currentSeries = Array.isArray(selected.series) ? selected.series : [];
    const previousSeries = Array.isArray(selected.previousSeries)
      ? selected.previousSeries
      : Array.from({ length: currentSeries.length }, () => 0);

    // For last7Days labels, normalize to MMM DD for a cleaner look.
    const normalizedLabels =
      rangeKey === "last7Days"
        ? labels.map((l) => {
            const d = dayjs(l, ["MMM D", "MMM DD", "MMM D, YYYY", "MMM DD, YYYY"], true);
            return d.isValid() ? d.format("MMM DD") : String(l);
          })
        : labels;

    return { labels: normalizedLabels, currentSeries, previousSeries };
  }, [rangeKey, resolvedGrowthRanges, resolvedUsersList, selectedMonth, selectedYear]);

  const validCreatedAtCount = useMemo(() => {
    return countUsersWithValidCreatedAt(resolvedUsersList || []);
  }, [resolvedUsersList]);

  const firstCreatedAtSample = useMemo(() => {
    if (!Array.isArray(resolvedUsersList) || !resolvedUsersList.length) return null;
    const u = resolvedUsersList.find(
      (x) => x?.createdAt || x?.createdOn || x?.created_at || x?.created,
    );
    if (!u) return null;
    return (
      u.createdAt ||
      u.createdOn ||
      u.created_at ||
      u.created ||
      u?.identityUser?.createdAt ||
      u?.identityUser?.createdOn ||
      null
    );
  }, [resolvedUsersList]);

  const chartHasData = useMemo(() => {
    const sum = (arr = []) => arr.reduce((acc, n) => acc + (Number(n) || 0), 0);
    return (
      sum(chartInput.currentSeries) > 0 ||
      sum(chartInput.previousSeries) > 0
    );
  }, [chartInput.currentSeries, chartInput.previousSeries]);

  return (
    <Card
      className={classNames(className, "overflow-hidden")}
      styles={{
        header: { background: "rgba(0,0,0,0.02)" },
        body: { paddingTop: 12 },
      }}
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        {segmentTiles.map((t) => (
          <Col key={t.key} xs={24} sm={12} lg={6}>
            <SegmentTile
              title={t.title}
              value={t.value}
              up={t.up}
              percentage={t.percentage}
              active={segment === t.key}
              onClick={() => setSegment(t.key)}
            />
          </Col>
        ))}
      </Row>

      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Space orientation="vertical" size={2}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Range
          </Typography.Text>
          <Select
            size="middle"
            value={rangeKey}
            onChange={(val) => setRangeKey(val)}
            style={{ width: 180 }}
            options={RANGE_OPTIONS.map((opt) => ({ value: opt.key, label: opt.label }))}
          />
        </Space>

        <Space orientation="vertical" size={2}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Year
          </Typography.Text>
          <Select
            size="middle"
            value={selectedYear}
            onChange={(val) => {
              setSelectedYear(Number(val));
            }}
            style={{ width: 140 }}
            disabled={
              !availableYears.length || (rangeKey !== "byYear" && rangeKey !== "byMonth")
            }
            options={(availableYears.length ? availableYears : [new Date().getFullYear()]).map(
              (y) => ({ value: y, label: String(y) }),
            )}
          />
        </Space>

        <Space orientation="vertical" size={2}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Month
          </Typography.Text>
          <Select
            size="middle"
            value={selectedMonth}
            onChange={(val) => {
              setSelectedMonth(Number(val));
            }}
            style={{ width: 160 }}
            disabled={rangeKey !== "byMonth"}
            options={Array.from({ length: 12 }, (_, i) => ({
              value: i,
              label: monthName(i),
            }))}
          />
        </Space>
      </div>

      {Array.isArray(resolvedUsersList) && resolvedUsersList.length > 0 && validCreatedAtCount === 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="No valid timestamps found"
          description={
            <div style={{ fontSize: 12 }}>
              Users loaded but no valid timestamp found (tried <code>createdAt</code>/
              <code>createdOn</code>/<code>acceptedAt</code>), so the chart can’t plot growth.
              {firstCreatedAtSample && (
                <div style={{ marginTop: 6, opacity: 0.75 }}>
                  Sample value: <code>{String(firstCreatedAtSample)}</code>
                </div>
              )}
            </div>
          }
        />
      )}

      {Array.isArray(resolvedUsersList) && resolvedUsersList.length > 0 && validCreatedAtCount > 0 && !chartHasData && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="No sign-ups found for the selected period"
        />
      )}

      <ReactEchart
        echarts={echarts}
        option={getOptions(
          getThemeColor,
          chartInput.labels,
          chartInput.currentSeries,
          chartInput.previousSeries,
        )}
        style={{ height: "21.25rem" }}
      />
    </Card>
  );
}

UserGrowthAudienceCard.propTypes = {
  growthRanges: PropTypes.object,
  growthRangesCustomers: PropTypes.object,
  growthRangesStaff: PropTypes.object,
  growthRangesUnclassified: PropTypes.object,
  totalUsersLabel: PropTypes.string,
  users: PropTypes.array,
  usersCustomers: PropTypes.array,
  usersStaff: PropTypes.array,
  usersUnclassified: PropTypes.array,
  className: PropTypes.string,
};
